/*
* Report page
*/

import * as _ from 'lodash';
import * as React from 'react';

import * as ReportComponents from '../report-components';

export const Render = ({ registerFunc, refFunc, scrollToFunc, pageTitle = '', className = '', pageCount, reportData, ...rest }) => {
    const sectionName = ReportComponents.ReportSectionNames.fundingSummary;
    const ReportPageWithRegister = ReportComponents.withPageRegistration(ReportComponents.ReportPageHeaderFooter, sectionName, registerFunc, refFunc, scrollToFunc);
    const { formData, association, inventory, currentOrder, currentOrderChart, percentFunded, yearlySummaryData } = reportData;

    // COMMON
    const yearSummary = _.find(reportData.yearlySummaryData, ['year', formData.beginYear]);
    const showMinimum = _.get(currentOrderChart, 'showMinimum', true);
    const showCurrent = _.get(currentOrderChart, 'showCurrent', true);
    const startingBalance = _.toNumber(_.get(currentOrder, 'startingBalance', 0));
    const begBalance = _.toNumber(_.get(currentOrderChart, 'begBalance', startingBalance));
    const idealBalance = _.toNumber(_.get(yearSummary, 'ffb', begBalance));
    const percentageFunded = percentFunded > 0 ? percentFunded / 100 : 0;

    const recMrcPerUnit = (_.get(currentOrderChart, 'recommendedMRCVal', 0) > 0 && _.get(association, 'details.numberUnits', 0) > 0) ? currentOrderChart.recommendedMRCVal / association.details.numberUnits : 0;
    const minMrcPerUnit = (_.get(currentOrderChart, 'minimumMRCVal', 0) > 0 && _.get(association, 'details.numberUnits', 0) > 0) ? currentOrderChart.minimumMRCVal / association.details.numberUnits : 0;
    const showMinMrcPerUnit = (_.get(association, 'details.numberUnits', 0) === 0 || _.get(currentOrderChart, 'hideNumberOfUnits')) ?
        <div /> : <ReportComponents.OffsetData xsOffset={2} title="Per Unit"><ReportComponents.Currency value={minMrcPerUnit} decimals={2} /></ReportComponents.OffsetData>;

    const minimumRecommendedMRC = (showMinimum) ? (
        <div className="pdf-report-funding-section-spacer">
            <ReportComponents.OffsetData title="Minimum Recommended MRC"><ReportComponents.Currency value={currentOrderChart.minimumMRCVal} decimals={0} xsOffset={2} /></ReportComponents.OffsetData>
            {showMinMrcPerUnit}

            <ReportComponents.OffsetData title="Future Annual Increases"><ReportComponents.Percentage value={currentOrderChart.minimumAnnualIncrease} decimals={2} /></ReportComponents.OffsetData>
            <ReportComponents.OffsetData xsOffset={2} title="For number of years:">{currentOrderChart.minimumYears}</ReportComponents.OffsetData>
            <ReportComponents.OffsetData xsOffset={2} title="Increases thereafter:"><ReportComponents.Percentage value={currentOrderChart.minimumSecondaryAnnualIncrease} decimals={2} /></ReportComponents.OffsetData>
        </div>
    ) : (<div />);

    // CHANGES FROM PRIOR YEAR
    const curMrc = _.get(currentOrderChart, 'currentMRCVal', 0);
    const changesRecIncreaseDollar = _.get(currentOrderChart, 'recommendedMRCVal', 0) - curMrc;
    const changesRecIncreasePercent = (curMrc !== 0 && changesRecIncreaseDollar !== 0) ? changesRecIncreaseDollar / curMrc : 0;

    const changesMinIncreaseDollar = _.get(currentOrderChart, 'minimumMRCVal', 0) - curMrc;
    const changesMinIncreasePercent = (curMrc !== 0 && changesRecIncreaseDollar !== 0) ? changesMinIncreaseDollar / curMrc : 0;

    const minimumChanges = (showMinimum) ? (
        <div className="pdf-report-funding-section-spacer">
            <ReportComponents.OffsetData title="Minimum Recommended Increase to Reserve Contribution">
                <ReportComponents.Currency value={changesMinIncreaseDollar} decimals={0} />
            </ReportComponents.OffsetData>
            <ReportComponents.OffsetData xsOffset={2} title="as Percentage">
                <ReportComponents.Percentage value={changesMinIncreasePercent} decimals={0} />
            </ReportComponents.OffsetData>
        </div>
    ) : (<div />);

    const currentBudgeted = (showCurrent) ? (
        <ReportComponents.OffsetData title="Budgeted Monthly Reserve Contribution">
            <ReportComponents.Currency value={curMrc} decimals={0} />
        </ReportComponents.OffsetData>
    ) : ('');

    const changesFromPriorYear = (showCurrent) ? (
        <ReportComponents.FundingSection title="Changes From Prior Year">
            <ReportComponents.OffsetData title="Recommended Increase to Reserve Contribution"><ReportComponents.Currency value={changesRecIncreaseDollar} decimals={0} /></ReportComponents.OffsetData>
            <ReportComponents.OffsetData xsOffset={2} title="as Percentage"><ReportComponents.Percentage value={changesRecIncreasePercent} decimals={0} /></ReportComponents.OffsetData>

            {minimumChanges}
        </ReportComponents.FundingSection>

    ) : ('');

    const buildAdditionalAssessments = (assessments: any) => {
        let results = _.stubArray();
        _.forEach(assessments, (entry) => {
            const titleString = 'Recommended Special Assessment (FY ' + entry.year + ')';
            results.push(
                <ReportComponents.OffsetData title={titleString}><ReportComponents.Currency value={entry.amount} decimals={0} /></ReportComponents.OffsetData>
            );
        });

        return results;
    };

    const specialAssessmentsChartRaw = _.get(currentOrderChart, 'specialAssessments', []);
    const specialAssessmentsChart = (_.size(specialAssessmentsChartRaw) > 0) ? _.get(specialAssessmentsChartRaw, [0]) : [];
    const showAdditionalAssessments = (_.size(specialAssessmentsChart) > 0);
    const additionalAssessments = (showAdditionalAssessments) ? buildAdditionalAssessments(specialAssessmentsChart) : (<div />);
    const singularAssessment = (showAdditionalAssessments) ? <div /> :
        <ReportComponents.OffsetData title={'Recommended Special Assessment (FY ' + currentOrderChart.beginYear + ')'}>
            <ReportComponents.Currency value={currentOrderChart.sa} decimals={0} />
        </ReportComponents.OffsetData>;

    const numberOfUnits = (_.get(currentOrderChart, 'hideNumberOfUnits', false)) ?
        <div /> : (_.get(association, 'details.numberUnits', 0) > 0) ?
            <ReportComponents.OffsetData title="# of units">{association.details.numberUnits}</ReportComponents.OffsetData> : <div />;
    const showRecMrcPerUnit = (_.get(association, 'details.numberUnits', 0) === 0 || _.get(currentOrderChart, 'hideNumberOfUnits')) ?
        <div /> : <ReportComponents.OffsetData xsOffset={2} title="Per Unit"><ReportComponents.Currency value={recMrcPerUnit} decimals={2} /></ReportComponents.OffsetData>;

    return (
        <ReportPageWithRegister title={pageTitle} className={className} totalPageCount={pageCount} showFooter={true} footerData={reportData}>
            <div className="pdf-funding-summary-table">
                <ReportComponents.FundingSection title="Beginning Assumptions">
                    {numberOfUnits}
                    <ReportComponents.OffsetData title="Fiscal Year End">{association.details.fiscalYearEnd}</ReportComponents.OffsetData>
                    {currentBudgeted}
                    <ReportComponents.OffsetData title="Projected Starting Reserve Balance">
                        <ReportComponents.Currency value={begBalance} decimals={0} />
                    </ReportComponents.OffsetData>
                    <ReportComponents.OffsetData title="Ideal Starting Reserve Balance">
                        <ReportComponents.Currency value={idealBalance} decimals={0} />
                    </ReportComponents.OffsetData>
                </ReportComponents.FundingSection>

                <ReportComponents.FundingSection title="Economic Assumptions">
                    <ReportComponents.OffsetData title="Current Inflation Rate"><ReportComponents.Percentage value={currentOrderChart.inflation} decimals={2} /></ReportComponents.OffsetData>
                    <ReportComponents.OffsetData title="Reported After-Tax Interest Rate"><ReportComponents.Percentage value={currentOrderChart.interest} decimals={2} /></ReportComponents.OffsetData>
                </ReportComponents.FundingSection>

                <ReportComponents.FundingSection title="Current Reserve Status">
                    <ReportComponents.OffsetData title="Current Balance as a % of Ideal Balance"><ReportComponents.Percentage value={percentageFunded} decimals={0} /></ReportComponents.OffsetData>
                </ReportComponents.FundingSection>

                <ReportComponents.FundingSection title="Recommendations">
                    {singularAssessment}
                    {additionalAssessments}
                    <ReportComponents.OffsetData title="Recommended Monthly Reserve Contribution"><ReportComponents.Currency value={currentOrderChart.recommendedMRCVal} decimals={0} /></ReportComponents.OffsetData>
                    {showRecMrcPerUnit}

                    <ReportComponents.OffsetData title="Future Annual Increases"><ReportComponents.Percentage value={currentOrderChart.recommendedAnnualIncrease} decimals={2} /></ReportComponents.OffsetData>
                    <ReportComponents.OffsetData xsOffset={2} title="For number of years:">{currentOrderChart.recommendedYears}</ReportComponents.OffsetData>
                    <ReportComponents.OffsetData xsOffset={2} title="Increases thereafter:"><ReportComponents.Percentage value={currentOrderChart.recommendedSecondaryAnnualIncrease} decimals={2} /></ReportComponents.OffsetData>

                    {minimumRecommendedMRC}
                </ReportComponents.FundingSection>

                {changesFromPriorYear}
                {/* THERE APPEARS TO BE A SUM OF SPECIAL ASSESSMENTS SHOWN HERE */}
            </div>
        </ReportPageWithRegister>
    );
};



// WEBPACK FOOTER //
// ./src/report/pdf/pages/report-page-funding-summary.tsx